/* ============================================================
   NalogConnectionCard — 15c2a311 (12754a4b part 2): the «Мой налог»
   self-employed profile on the provider cabinet profile tab, next to
   the payout card. Not connected → CTA that opens the SMS wizard
   popup (phone → code → done). Connected → token-free profile card
   (ФИО/ИНН/phone/status) with reconnect (wizard again) and
   disconnect (server wipes tokens, receipts stay for history).
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import type { NalogConnection } from '../types/nalog';
import { buildNalogCardView, isValidNalogPhone, isValidNalogSmsCode } from './nalogView';

const inputStyle: CSSProperties = {
  width: '100%',
  height: 48,
  padding: '12px 16px',
  fontSize: 'var(--fs-h3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  letterSpacing: '0.08em',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function NalogConnectionCard() {
  const [connection, setConnection] = useState<NalogConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setConnection(await api.getNalogConnection());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.disconnectNalog();
      setConfirmDisconnect(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отключить');
    } finally {
      setBusy(false);
    }
  };

  const view = connection ? buildNalogCardView(connection, formatDate) : null;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)', maxWidth: 560 }}>
      <div>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Мой налог (НПД)</h2>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Чеки формируются автоматически при каждой выплате Safe Deal. Подключается по СМС —
          как в приложении «Мой налог».
        </p>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '0 auto' }} />
      ) : view ? (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-xs)',
              padding: 'var(--space-md)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-background)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
              <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)' }}>
                {view.lines[0]?.value ?? 'Профиль НПД'}
              </span>
              <span className={view.badgeClass}>{view.statusLabel}</span>
            </div>
            {view.lines.slice(1).map((line) => (
              <p key={line.label} style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                {line.label}: <span style={{ color: 'var(--color-text-primary)' }}>{line.value}</span>
              </p>
            ))}
          </div>

          {view.problemText && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
              Последняя ошибка: {view.problemText}
            </p>
          )}

          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}

          {confirmDisconnect ? (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', flex: 1, minWidth: 200 }}>
                Отключить профиль? Токены будут удалены, оформленные чеки останутся в истории.
              </span>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void disconnect()}>
                {busy ? 'Отключение…' : 'Отключить'}
              </button>
              <button type="button" className="btn btn-tertiary" disabled={busy} onClick={() => setConfirmDisconnect(false)}>
                Отмена
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              {view.canReconnect && (
                <button type="button" className="btn btn-primary" onClick={() => setWizardOpen(true)}>
                  {view.status === 'disconnected' ? 'Подключить снова' : 'Переподключить'}
                </button>
              )}
              {view.canDisconnect && (
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDisconnect(true)}>
                  Отключить
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', flex: 1, minWidth: 180 }}>
              Профиль не подключён — чеки по выплатам не формируются.
            </span>
            <button type="button" className="btn btn-primary" onClick={() => setWizardOpen(true)}>
              Подключить
            </button>
          </div>
        </>
      )}

      {wizardOpen && (
        <NalogWizardPopup
          onClose={() => setWizardOpen(false)}
          onConnected={() => {
            setWizardOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

/* ── SMS wizard popup: phone → code → done ─────────────────── */

function NalogWizardPopup({ onClose, onConnected }: { onClose: () => void; onConnected: () => void }) {
  const [step, setStep] = useState<'phone' | 'code' | 'done'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'done') onClose();
    };
    window.addEventListener('keydown', onKey);
    phoneRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  const submitPhone = async () => {
    setBusy(true);
    setError(null);
    try {
      const ack = await api.startNalogConnect(phone.trim());
      setExpiresAt(ack.expiresAtUtc);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить СМС');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.verifyNalogConnect(code.trim());
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Код не подтверждён');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Подключение Мой налог"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-lg)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== 'done') onClose();
      }}
    >
      <div
        className="card"
        style={{
          width: 'min(420px, 100%)',
          boxShadow: 'var(--shadow-modal)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-md)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
            Подключение «Мой налог»
          </h3>
          {step !== 'done' && (
            <button type="button" className="btn btn-tertiary" style={{ fontSize: 'var(--fs-h3)', padding: '4px 10px' }} onClick={onClose}>
              ✕
            </button>
          )}
        </div>

        {step === 'phone' && (
          <>
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Шаг 1 из 2. Введите номер телефона, привязанный к профилю самозанятого, — на него придёт код из СМС.
            </p>
            <input
              ref={phoneRef}
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValidNalogPhone(phone)) void submitPhone();
              }}
              placeholder="+7 999 123-45-67"
              autoComplete="tel"
              style={inputStyle}
            />
            {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
            <button type="button" className="btn btn-primary" disabled={busy || !isValidNalogPhone(phone)} onClick={() => void submitPhone()}>
              {busy ? 'Отправка…' : 'Получить код'}
            </button>
          </>
        )}

        {step === 'code' && (
          <>
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Шаг 2 из 2. Код из СМС отправлен на {phone.trim() || 'ваш номер'}.
              {expiresAt && ` Код действует до ${new Date(expiresAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}.`}
            </p>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValidNalogSmsCode(code)) void submitCode();
              }}
              placeholder="Код из СМС"
              autoFocus
              style={inputStyle}
            />
            {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setStep('phone'); setError(null); }}>
                ← Другой номер
              </button>
              <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled={busy || !isValidNalogSmsCode(code)} onClick={() => void submitCode()}>
                {busy ? 'Проверка…' : 'Подтвердить'}
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-success)', fontWeight: 'var(--fw-semibold)' }}>
              ✓ Профиль подключён
            </p>
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Чеки будут формироваться автоматически при каждой выплате Safe Deal.
            </p>
            <button type="button" className="btn btn-primary" onClick={onConnected}>
              Готово
            </button>
          </>
        )}
      </div>
    </div>
  );
}
